import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { str_isEmpty } from '@Util/Utils';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import Table_Head from '@Elements/Table/Table_Head';
import Table_Data from '@Elements/Table/Table_Data';

dayjs.extend(duration);

import {
    Customers_Total_Time_Table_Props,
    Time_Log_Entry,
    Customers_Total_Time_Table_Type
} from '@Util/Types';

export default function Customers_Total_Time_Table(props: Customers_Total_Time_Table_Props): JSX.Element {
    const [t] = useTranslation();
    const [sort_direction, set_sort_direction] = useState<"Ascending" | "Descending">("Ascending");
    const [active_sorting_column, set_active_sorting_column] = useState<"customer_name" | "total_time">("customer_name");

    const table_data: { data: Customers_Total_Time_Table_Type[], total: Customers_Total_Time_Table_Type } = useMemo(() => {
        const customer_times: { [key: string]: number } = {};

        props.logs.forEach((log: Time_Log_Entry) => {
            const customer_name: string = log.customerName;
            const total_time: number = (new Date(log.endDate).getTime() - new Date(log.startDate).getTime()) / 60000; // convert ms to minutes

            if (str_isEmpty(customer_name)) {
                throw new Error("Customers Total Time Table: Customer name is empty");
            }

            customer_times[customer_name] = (customer_times[customer_name] || 0) + total_time;
        });

        let temp_data: Customers_Total_Time_Table_Type[] = Object.entries(customer_times).map(([customer_name, total_time]) => ({
            customer_name: customer_name,
            total_time: dayjs.duration(total_time, 'minutes').format('H [hours], m [minutes] (H.m [h])')
        }));

        let total: number = Object.values(customer_times).reduce((a, b) => a + b, 0);
        let totalHours: number = Math.floor(total / 60);
        let totalMinutes: number = total % 60;

        const totalRow: { customer_name: string, total_time: string } = {
            customer_name: "Total",
            total_time: `${totalHours} hours, ${totalMinutes} minutes (${totalHours}.${totalMinutes} h)`
        };

        return {
            data: temp_data.sort((a: Customers_Total_Time_Table_Type, b: Customers_Total_Time_Table_Type) =>
                String(a[active_sorting_column]).localeCompare(String(b[active_sorting_column]), undefined, { numeric: true, sensitivity: 'base' }) * (sort_direction === "Ascending" ? 1 : -1)
            ),
            total: totalRow
        };
    }, [props.logs, sort_direction, active_sorting_column]);

    function handle_set_sorting_on_table_head(col: string): void {
        if (str_isEmpty(col)) {
            throw new Error("Handle sorting on table: Column is empty");
        }

        if (col === active_sorting_column) {
            if (sort_direction == "Ascending") {
                return set_sort_direction("Descending");
            } else {
                return set_sort_direction("Ascending");
            }
        }

        set_active_sorting_column(col as "customer_name" | "total_time");
        set_sort_direction("Ascending");
    }

    function Empty_Table(): JSX.Element {
        return (
            <tr>
                <Table_Data align="left">
                    <span className="Table_Data_Customer_Name">{t('Text.NoProjectsToShow')}</span>
                </Table_Data>
                <Table_Data align="right">
                    <></>
                </Table_Data>
            </tr>
        )
    }

    return (
        <>
            <div className="Table_Wrapper">
                <table className="Table">
                    <thead>
                        <tr>
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Customer')}
                                sortingAction={() => handle_set_sorting_on_table_head('customer_name')}
                                sortingDataName="customer_name"
                                style={{ width: "50%" }}

                            />
                            <Table_Head
                                align="right"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Time')}
                                sortingAction={() => handle_set_sorting_on_table_head('total_time')}
                                sortingDataName="total_time"
                                style={{ width: "50%" }}

                            />
                        </tr>
                    </thead>
                    <tbody>
                        {
                            table_data.data.isEmpty() ?
                                <Empty_Table /> :
                                <>
                                    {
                                        table_data.data.map((customer: Customers_Total_Time_Table_Type, index: number) =>
                                            <tr key={`projects_table_row${index}`}>
                                                <Table_Data
                                                    align="left"
                                                    style={{ width: "50%" }}
                                                >
                                                    {customer.customer_name}
                                                </Table_Data>
                                                <Table_Data
                                                    align="right"
                                                    style={{ width: "50%" }}
                                                >
                                                    {customer.total_time}
                                                </Table_Data>
                                            </tr>
                                        )}
                                    <tr>
                                        <Table_Data
                                            align="left"
                                            style={{ width: "50%" }}
                                        >
                                            {table_data.total.customer_name}
                                        </Table_Data>
                                        <Table_Data
                                            align="right"
                                            style={{ width: "50%" }}
                                        >
                                            {table_data.total.total_time}
                                        </Table_Data>
                                    </tr>
                                </>
                        }
                    </tbody>
                </table>
            </div>
        </>
    );
}